/*
Not to be confused for comment-macro-extension-handler.tsx in the same package which is used specifically
for the comment editor. This MacroExtensionHandler file is used for creating extension handlers
for next/full-page-editor
*/
import React, { Fragment } from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import type { EditorActions } from '@atlaskit/editor-core';

import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import {
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { resolveQuestionsMacroExtensionKey } from '@confluence/fabric-extension-lib/entry-points/macroKeyResolver.ts';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { PagePropertiesReportRenderer } from '@confluence/page-properties-report-renderer';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { GlobalQuestionsListMacroHandler } from '@confluence/questions/entry-points/GlobalQuestionsListMacroHandler.ts';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';

import { AnchorMacroRenderer } from './AnchorMacroRenderer.tsx';
import { DeadMacroRenderer } from './DeadMacroRenderer/index.ts';
import { CreateFromTemplate } from './CreateFromTemplate/index.ts';
import { getMigrationExtHandlerForEditor } from './MigrationExtension/index.ts';
import { ExcerptIncludeExtensionHandler } from './ExcerptInclude/index.ts';
import { TableOfContentsLiveComponent } from './TableOfContentsLiveComponent.tsx';
import { LiveSearchHandler } from './LiveSearch/LiveSearchHandler.tsx';

type Options = {
	contentId: string;
	spaceKey: string;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	editorActions?: EditorActions;
	subscribeDocChange: SubscriptionHandlerFnType;
	unsubscribeDocChange: SubscriptionHandlerFnType;
	isLivePage?: boolean;
	isCollabEditForLiveUpdatesEnabled?: boolean;
};

export const getFullPageExtensionHandlers = ({
	contentId,
	spaceKey,
	extensionContextProvider,
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}: Options): ExtensionHandlers => {
	return {
		[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForEditor({
			contentId,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
			isCollabEditForLiveUpdatesEnabled,
		}),
		[EXTENSION_TYPE.MIGRATION]: getMigrationExtHandlerForEditor({
			contentId,
			contextProviders: {}, // we do not yet need ClassicEditorContextProvider for InvalidExtension in next editor
		}),
	};
};

export const getMacroExtensionHandlerForEditor = ({
	contentId,
	spaceKey,
	extensionContextProvider = {
		ClassicEditorContextProvider: ({
			children,
			isWithinEditor: _isWithinEditor,
		}: {
			children: React.ReactNode;
			isWithinEditor?: boolean;
		}) => <>{children}</>,
	},
	editorActions,
	subscribeDocChange,
	unsubscribeDocChange,
	isLivePage,
	isCollabEditForLiveUpdatesEnabled,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension, doc) => {
		const resolvedExtensionKey = resolveQuestionsMacroExtensionKey(extension.extensionKey);
		const extensionKey = resolvedExtensionKey;

		// Update extension if key was resolved to a different value
		if (resolvedExtensionKey !== extension.extensionKey) {
			extension = { ...extension, extensionKey: resolvedExtensionKey };
		}

		const attributes = getMacroAttributesFromADFNode(extension);
		const { ClassicEditorContextProvider } = extensionContextProvider;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			references: doc?.[0]?.references || [],
			contentId,
			spaceKey,
			mode: EDITOR,
			extensionKey,
			attributes,
			multiBodiedExtensionActions: doc?.[0]?.actions,
		};

		let implementation;

		const legacyMacroRenderer = (
			<ClassicEditorContextProvider>
				<LegacyMacroRenderer {...macroDefaultProps} />
			</ClassicEditorContextProvider>
		);

		switch (extensionKey) {
			// blocklist - block extensions - we want to show the editor's placeholder frame, so return empty div
			case 'toc': // toc needs to be rewritten as it accesses the DOM to create its tree
				implementation = (
					<TableOfContentsLiveComponent
						macroDefaultProps={macroDefaultProps}
						editorActions={editorActions}
						subscribeDocChange={subscribeDocChange}
						unsubscribeDocChange={unsubscribeDocChange}
						isCollabEditForLiveUpdatesEnabled={!!isCollabEditForLiveUpdatesEnabled}
					/>
				);
				break;

			case 'create-from-template':
				implementation = <CreateFromTemplate {...macroDefaultProps} />;
				break;

			// blocklist - inline extensions - the editor doesn't render a placeholder frame, so we need to display something
			case 'pagetree': // pagetree is not spa friendly and will only work sometimes
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
				break;

			case 'anchor':
				implementation = <AnchorMacroRenderer {...macroDefaultProps} />;
				break;

			case 'excerpt-include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<ExcerptIncludeExtensionHandler
							{...macroDefaultProps}
							isLivePage={isLivePage}
							Renderer={PageContentRenderer}
						/>
					</ClassicEditorContextProvider>
				);
				break;

			case 'detailssummary':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<PagePropertiesReportRenderer
							macroDefaultProps={macroDefaultProps}
							macroParams={extension?.parameters?.macroParams}
							openAnchorsInNewTab
						/>
					</ClassicEditorContextProvider>
				);
				break;
			case 'profile-picture':
				implementation = (
					<ProfilePictureMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'profile':
				implementation = (
					<UserProfileMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;
			case 'include':
				implementation = (
					<ClassicEditorContextProvider isWithinEditor>
						<LegacyMacroRenderer {...macroDefaultProps} />
					</ClassicEditorContextProvider>
				);
				break;
			case 'livesearch':
				implementation = <LiveSearchHandler macroDefaultProps={macroDefaultProps} />;
				break;
			case 'questionslist-macro':
				implementation = legacyMacroRenderer;
				break;
			case 'questionslist-macro-native':
				implementation = (
					<GlobalQuestionsListMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
				break;

			// generic macro renderer
			default:
				implementation = legacyMacroRenderer;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={contentId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
